"use client"

import { useState, useEffect } from "react"

export default function MeritTransferForm() {
  const [activeMenuItem, setActiveMenuItem] = useState("Online Prayer Requests")
  const [isMobile, setIsMobile] = useState(false)
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [errors, setErrors] = useState({})
  const [isSubmitting, setIsSubmitting] = useState(false)

  // Form state - Updated to match the template requirements
  const [formData, setFormData] = useState({
    // Memorial details
    deceased_name: "",
    relationship: "",
    relationship_other: "",
    date_of_transition: "",
    offering_amount: "",

    // Requester details
    requester_name: "",
    lineage: "",
    division: "",
    todays_date: new Date().toISOString().split("T")[0],
  })

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768)
    }
    handleResize()
    window.addEventListener("resize", handleResize)
    return () => window.removeEventListener("resize", handleResize)
  }, [])

  // Custom SVG Icons
  const MenuIcon = () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
      <path d="M3,6H21V8H3V6M3,11H21V13H3V11M3,16H21V18H3V16Z" />
    </svg>
  )

  const ArrowLeftIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
      <path d="M20,11V13H8L13.5,18.5L12.08,19.92L4.16,12L12.08,4.08L13.5,5.5L8,11H20Z" />
    </svg>
  )

  const ShoppingCartIcon = () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
      <path d="M17,18C15.89,18 15,18.89 15,20A2,2 0 0,0 17,22A2,2 0 0,0 19,20C19,18.89 18.1,18 17,18M1,2V4H3L6.6,11.59L5.24,14.04C5.09,14.32 5,14.65 5,15A2,2 0 0,0 7,17H19V15H7.42A0.25,0.25 0 0,1 7.17,14.75C7.17,14.7 7.18,14.66 7.2,14.63L8.1,13H15.55C16.3,13 16.96,12.58 17.3,11.97L20.88,5H5.21L4.27,3H1M7,18C5.89,18 5,18.89 5,20A2,2 0 0,0 7,22A2,2 0 0,0 9,20C9,18.89 8.1,18 7,18Z" />
    </svg>
  )

  const HomeIcon = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
      <path d="M10,20V14H14V20H19V12H22L12,3L2,12H5V20H10Z" />
    </svg>
  )

  const PencilIcon = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
      <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z" />
    </svg>
  )

  const UploadIcon = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
      <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z" />
      <path d="M12,11L16,15H13V19H11V15H8L12,11Z" />
    </svg>
  )

  const DownloadIcon = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
      <path d="M5,20H19V18H5M19,9H15V3H9V9H5L12,16L19,9Z" />
    </svg>
  )

  const PaymentIcon = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
      <path d="M20,4H4C2.89,4 2,4.89 2,6V18A2,2 0 0,0 4,20H20A2,2 0 0,0 22,18V6C22,4.89 21.11,4 20,4M20,18H4V12H20V18M20,8H4V6H20V8Z" />
    </svg>
  )

  const LogOutIcon = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
      <path d="M16,17V14H9V10H16V7L21,12L16,17M14,2A2,2 0 0,1 16,4V6H14V4H5V20H14V18H16V20A2,2 0 0,1 14,22H5A2,2 0 0,1 3,20V4A2,2 0 0,1 5,2H14Z" />
    </svg>
  )

  const ChevronRightIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
      <path d="M8.59,16.58L13.17,12L8.59,7.41L10,6L16,12L10,18L8.59,16.58Z" />
    </svg>
  )

  const menuItems = [
    { name: "Home", icon: HomeIcon, hasSubmenu: true },
    { name: "Online Prayer Requests", icon: PencilIcon, hasSubmenu: true },
    { name: "Upload Prayer Forms", icon: UploadIcon, hasSubmenu: false },
    { name: "Download Prayer Forms", icon: DownloadIcon, hasSubmenu: true },
    { name: "Payments & Offerings", icon: PaymentIcon, hasSubmenu: false },
    { name: "My Basket", icon: ShoppingCartIcon, hasSubmenu: false },
    { name: "Sign Out", icon: LogOutIcon, hasSubmenu: false },
  ]

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }))
    // Clear field error when user types
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: null }))
    }
  }

  const validateForm = () => {
    const newErrors = {}

    // Validate all required fields
    if (!formData.deceased_name.trim()) {
      newErrors.deceased_name = "Name or Posthumous Title of the Deceased is required"
    }
    if (!formData.relationship.trim()) {
      newErrors.relationship = "Relationship is required"
    }
    if (formData.relationship === "Other" && !formData.relationship_other.trim()) {
      newErrors.relationship_other = "Please specify the relationship"
    }
    if (!formData.date_of_transition) {
      newErrors.date_of_transition = "Date of Transition is required"
    }
    if (!formData.offering_amount.trim()) {
      newErrors.offering_amount = "Offering amount is required"
    }
    if (!formData.requester_name.trim()) {
      newErrors.requester_name = "Requester's name is required"
    }
    if (!formData.lineage.trim()) {
      newErrors.lineage = "Lineage is required"
    }
    if (!formData.division.trim()) {
      newErrors.division = "Division is required"
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!validateForm()) return

    setIsSubmitting(true)
    try {
      console.log("Merit Transfer Form Data:", formData)
      // Simulate API call
      await new Promise((resolve) => setTimeout(resolve, 2000))
      alert("Merit Transfer Request submitted successfully!")

      // Reset form
      setFormData({
        deceased_name: "",
        relationship: "",
        relationship_other: "",
        date_of_transition: "",
        offering_amount: "",
        requester_name: "",
        lineage: "",
        division: "",
        todays_date: new Date().toISOString().split("T")[0],
      })
    } catch (error) {
      console.error("Error submitting form:", error)
      alert("Error submitting form. Please try again.")
    } finally {
      setIsSubmitting(false)
    }
  }

  const styles = {
    container: {
      display: "flex",
      minHeight: "100vh",
      backgroundColor: "#f5f5f5",
      fontFamily: "Arial, sans-serif",
      position: "relative",
    },
    header: {
      position: "fixed",
      top: 0,
      left: 0,
      right: 0,
      height: "80px",
      backgroundColor: "#6b7280",
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      padding: "0 20px",
      zIndex: 1000,
      boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
    },
    headerLeft: {
      display: "flex",
      alignItems: "center",
      gap: "20px",
    },
    headerRight: {
      display: "flex",
      alignItems: "center",
      gap: "15px",
    },
    menuButton: {
      background: "none",
      border: "none",
      color: "white",
      fontSize: "1.5rem",
      cursor: "pointer",
      display: "flex",
      alignItems: "center",
      padding: "8px",
    },
    goBackButton: {
      background: "none",
      border: "none",
      color: "white",
      display: "flex",
      alignItems: "center",
      gap: "8px",
      cursor: "pointer",
      fontSize: "1rem",
      padding: "8px 12px",
    },
    cartIcon: {
      position: "relative",
      color: "white",
      fontSize: "1.5rem",
      cursor: "pointer",
      padding: "8px",
    },
    cartBadge: {
      position: "absolute",
      top: "0px",
      right: "0px",
      backgroundColor: "#ef4444",
      color: "white",
      borderRadius: "50%",
      width: "20px",
      height: "20px",
      fontSize: "0.75rem",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    },
    signOutButton: {
      backgroundColor: "#9ca3af",
      color: "white",
      border: "none",
      padding: "8px 16px",
      borderRadius: "4px",
      cursor: "pointer",
      fontSize: "0.9rem",
    },
    sidebar: {
      backgroundColor: "#7fb3a3",
      paddingTop: "80px",
      minHeight: "100vh",
      position: "fixed",
      width: isMobile ? "80%" : "250px",
      left: isMobile ? (sidebarOpen ? "0" : "-100%") : "0",
      transition: "left 0.3s ease",
      zIndex: 999,
      overflowY: "auto",
    },
    overlay: {
      position: "fixed",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: "rgba(0,0,0,0.5)",
      zIndex: 998,
      display: isMobile && sidebarOpen ? "block" : "none",
    },
    menuItem: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      padding: "15px 20px",
      color: "white",
      cursor: "pointer",
      borderBottom: "1px solid rgba(255,255,255,0.1)",
      fontSize: "0.95rem",
      transition: "background-color 0.2s",
      borderTopLeftRadius: "30px",
      borderBottomLeftRadius: "30px",
      marginBottom: "20px",
    },
    menuItemActive: {
      backgroundColor: "#4b5563",
    },
    menuItemLeft: {
      display: "flex",
      alignItems: "center",
      gap: "12px",
    },
    mainContent: {
      marginLeft: isMobile ? "0" : "250px",
      paddingTop: "80px",
      flex: 1,
      padding: isMobile ? "20px" : "30px",
      width: isMobile ? "100%" : "calc(100% - 250px)",
    },
    formContainer: {
      maxWidth: "900px",
      margin: "0 auto",
    },
    pageTitle: {
      fontSize: "28px",
      fontWeight: "700",
      color: "#374151",
      textAlign: "center",
      marginBottom: "8px",
    },
    pageSubtitle: {
      fontSize: "18px",
      fontWeight: "600",
      color: "#7fb3a3",
      textAlign: "center",
      marginBottom: "16px",
    },
    pageDescription: {
      fontSize: "16px",
      color: "#6b7280",
      textAlign: "center",
      marginBottom: "32px",
    },
    card: {
      backgroundColor: "white",
      borderRadius: "8px",
      boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
      padding: "24px",
      marginBottom: "24px",
    },
    cardTitle: {
      fontSize: "18px",
      fontWeight: "600",
      color: "#374151",
      marginBottom: "16px",
    },
    formGrid: {
      display: "grid",
      gridTemplateColumns: isMobile ? "1fr" : "repeat(2, 1fr)",
      gap: "20px",
    },
    formGroup: {
      display: "flex",
      flexDirection: "column",
      gap: "8px",
      marginBottom: "16px",
    },
    label: {
      fontSize: "14px",
      fontWeight: "500",
      color: "#374151",
    },
    input: {
      padding: "10px 12px",
      border: "1px solid #d1d5db",
      borderRadius: "4px",
      fontSize: "14px",
      width: "100%",
      outline: "none",
      transition: "border-color 0.2s",
    },
    inputFocus: {
      borderColor: "#7fb3a3",
    },
    textarea: {
      padding: "10px 12px",
      border: "1px solid #d1d5db",
      borderRadius: "4px",
      fontSize: "14px",
      width: "100%",
      outline: "none",
      minHeight: "120px",
      resize: "vertical",
      fontFamily: "Arial, sans-serif",
      transition: "border-color 0.2s",
    },
    inputError: {
      border: "1px solid #ef4444",
    },
    errorText: {
      color: "#ef4444",
      fontSize: "12px",
      marginTop: "4px",
    },
    submitButton: {
      backgroundColor: "#7fb3a3",
      color: "white",
      border: "none",
      padding: "12px 24px",
      borderRadius: "6px",
      fontSize: "16px",
      fontWeight: "600",
      cursor: "pointer",
      display: "block",
      margin: "32px auto 0",
      width: "fit-content",
      minWidth: "250px",
      textAlign: "center",
      transition: "background-color 0.2s",
    },
    submitButtonHover: {
      backgroundColor: "#6da394",
    },
    submitButtonDisabled: {
      opacity: "0.7",
      cursor: "not-allowed",
    },
  }

  return (
    <div style={styles.container}>
      {/* Header */}
      <header style={styles.header}>
        <div style={styles.headerLeft}>
          <button style={styles.menuButton} onClick={() => setSidebarOpen(!sidebarOpen)}>
            <MenuIcon />
          </button>
          <img
            src="/placeholder.svg?height=60&width=120"
            alt="Shinnyo Logo"
            style={{
              height: "60px",
              objectFit: "contain",
              display: isMobile && sidebarOpen ? "none" : "block",
            }}
          />
          {!isMobile && (
            <button style={styles.goBackButton} onClick={() => window.history.back()}>
              <ArrowLeftIcon />
              Go Back
            </button>
          )}
        </div>

        <div style={styles.headerRight}>
          <div style={styles.cartIcon}>
            <ShoppingCartIcon />
            <span style={styles.cartBadge}>0</span>
          </div>
          <button style={styles.signOutButton}>Sign Out</button>
        </div>
      </header>

      {/* Sidebar Overlay */}
      {isMobile && <div style={styles.overlay} onClick={() => setSidebarOpen(false)} />}

      {/* Sidebar */}
      <nav style={styles.sidebar}>
        {menuItems.map((item, index) => {
          const IconComponent = item.icon
          return (
            <div
              key={index}
              style={{
                ...styles.menuItem,
                ...(activeMenuItem === item.name ? styles.menuItemActive : {}),
              }}
              onClick={() => {
                setActiveMenuItem(item.name)
                if (isMobile) setSidebarOpen(false)
              }}
              onMouseEnter={(e) => {
                if (activeMenuItem !== item.name) {
                  e.target.style.backgroundColor = "#4b5563"
                }
              }}
              onMouseLeave={(e) => {
                if (activeMenuItem !== item.name) {
                  e.target.style.backgroundColor = "transparent"
                }
              }}
            >
              <div style={styles.menuItemLeft}>
                <IconComponent />
                <span>{item.name}</span>
              </div>
              {item.hasSubmenu && <ChevronRightIcon />}
            </div>
          )
        })}
      </nav>

      {/* Main Content */}
      <main style={styles.mainContent}>
        <div style={styles.formContainer}>
          <h1 style={styles.pageTitle}>MEMORIAL REQUEST</h1>
          <h2 style={styles.pageSubtitle}>Request for Spiritual Consolation on the Memorial (Butsuji Kuyo)</h2>

          <form onSubmit={handleSubmit}>
            {/* Memorial Details Section */}
            <div style={styles.card}>
              <h3 style={styles.cardTitle}>Request for Spiritual Consolation on the Memorial (Butsuji Kuyo)</h3>

              <div style={styles.formGroup}>
                <label style={styles.label}>Name or Posthumous Title of the Deceased *</label>
                <input
                  type="text"
                  style={{
                    ...styles.input,
                    ...(errors.deceased_name ? styles.inputError : {}),
                  }}
                  value={formData.deceased_name}
                  onChange={(e) => handleInputChange("deceased_name", e.target.value)}
                  placeholder="Enter name or posthumous title of the deceased"
                />
                {errors.deceased_name && <span style={styles.errorText}>{errors.deceased_name}</span>}
              </div>

              <div style={styles.formGrid}>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Relationship *</label>
                  <select
                    style={{
                      ...styles.input,
                      ...(errors.relationship ? styles.inputError : {}),
                    }}
                    value={formData.relationship}
                    onChange={(e) => handleInputChange("relationship", e.target.value)}
                  >
                    <option value="">Select relationship</option>
                    <option value="Maternal">Maternal</option>
                    <option value="Paternal">Paternal</option>
                    <option value="Other">Other</option>
                  </select>
                  {errors.relationship && <span style={styles.errorText}>{errors.relationship}</span>}
                </div>

                {formData.relationship === "Other" && (
                  <div style={styles.formGroup}>
                    <label style={styles.label}>Please specify relationship *</label>
                    <input
                      type="text"
                      style={{
                        ...styles.input,
                        ...(errors.relationship_other ? styles.inputError : {}),
                      }}
                      value={formData.relationship_other}
                      onChange={(e) => handleInputChange("relationship_other", e.target.value)}
                      placeholder="Specify relationship"
                    />
                    {errors.relationship_other && <span style={styles.errorText}>{errors.relationship_other}</span>}
                  </div>
                )}
              </div>

              <div style={styles.formGrid}>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Date of Transition *</label>
                  <input
                    type="date"
                    style={{
                      ...styles.input,
                      ...(errors.date_of_transition ? styles.inputError : {}),
                    }}
                    value={formData.date_of_transition}
                    onChange={(e) => handleInputChange("date_of_transition", e.target.value)}
                  />
                  {errors.date_of_transition && <span style={styles.errorText}>{errors.date_of_transition}</span>}
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label}>Offering (£) *</label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    style={{
                      ...styles.input,
                      ...(errors.offering_amount ? styles.inputError : {}),
                    }}
                    value={formData.offering_amount}
                    onChange={(e) => handleInputChange("offering_amount", e.target.value)}
                    placeholder="Enter offering amount"
                  />
                  {errors.offering_amount && <span style={styles.errorText}>{errors.offering_amount}</span>}
                </div>
              </div>
            </div>

            {/* Requester Details Section */}
            <div style={styles.card}>
              <h3 style={styles.cardTitle}>Requester Details</h3>

              <div style={styles.formGrid}>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Requester's Name *</label>
                  <input
                    type="text"
                    style={{
                      ...styles.input,
                      ...(errors.requester_name ? styles.inputError : {}),
                    }}
                    value={formData.requester_name}
                    onChange={(e) => handleInputChange("requester_name", e.target.value)}
                    placeholder="Enter your name"
                  />
                  {errors.requester_name && <span style={styles.errorText}>{errors.requester_name}</span>}
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label}>Lineage *</label>
                  <input
                    type="text"
                    style={{
                      ...styles.input,
                      ...(errors.lineage ? styles.inputError : {}),
                    }}
                    value={formData.lineage}
                    onChange={(e) => handleInputChange("lineage", e.target.value)}
                    placeholder="Enter lineage"
                  />
                  {errors.lineage && <span style={styles.errorText}>{errors.lineage}</span>}
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label}>Division *</label>
                  <input
                    type="text"
                    style={{
                      ...styles.input,
                      ...(errors.division ? styles.inputError : {}),
                    }}
                    value={formData.division}
                    onChange={(e) => handleInputChange("division", e.target.value)}
                    placeholder="Enter division"
                  />
                  {errors.division && <span style={styles.errorText}>{errors.division}</span>}
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label}>Today's Date *</label>
                  <input
                    type="date"
                    style={styles.input}
                    value={formData.todays_date}
                    onChange={(e) => handleInputChange("todays_date", e.target.value)}
                  />
                </div>
              </div>
            </div>

            <div style={{ display: "flex", gap: "16px", justifyContent: "center", marginTop: "32px" }}>
              <button
                type="submit"
                style={{
                  ...styles.submitButton,
                  ...(isSubmitting ? styles.submitButtonDisabled : {}),
                }}
                disabled={isSubmitting}
                onMouseEnter={(e) => {
                  if (!isSubmitting) {
                    e.target.style.backgroundColor = "#6da394"
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isSubmitting) {
                    e.target.style.backgroundColor = "#7fb3a3"
                  }
                }}
              >
                {isSubmitting ? "Submitting..." : "Submit Memorial Request"}
              </button>

              
            </div>
          </form>
        </div>
      </main>
    </div>
  )
}
